import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { Indicator, OdsPriorityGroups, OdsPriorityGroupsExtended, OdsPriorityGroupsWithGrouped } from '../../../interfaces/ODSPriorityGroups';
import { getODS, getPriorityGroups } from './odsPriorityGroupsThunk';
import { flattenEstructurales } from '../../../utils/estructurals';

interface OdsPriorityGroupsState {
    loadingODS: "idle" | "pending" | "succeeded" | "failed";
    loadingGroups: "idle" | "pending" | "succeeded" | "failed";
    selectedItems: Record<string, OdsPriorityGroups | OdsPriorityGroupsWithGrouped | null>;
    selectedPagination: {
        [module: string]: {
            currentPage: number;
            pageSize: number;
        };
    };
    ods: OdsPriorityGroups[] | null;
    errorsOds: boolean;
    showOds: boolean;
    priorityGroups: OdsPriorityGroupsWithGrouped[] | null;
    errorPriorityGroups: boolean;
    showPriorityGroups: boolean;
    selected_type_indicator: { name: string } | null;
}

const initialState: OdsPriorityGroupsState = {
    loadingODS: "idle",
    loadingGroups: "idle",
    selectedItems: {},
    selectedPagination: {},
    ods: null,
    errorsOds: false,
    showOds: false,
    priorityGroups: null,
    errorPriorityGroups: false,
    showPriorityGroups: false,
    selected_type_indicator: null,
};

export const OdsPriorityGroupsSlice = createSlice({
    name: 'ods-priorityGroups',
    initialState,
    reducers: {
        setSelectedItem: (
            state,
            action: PayloadAction<{ module: string; item: OdsPriorityGroups }>
        ) => {
            const { module, item } = action.payload;
            state.selectedItems[module] = item;
            state.selectedPagination[module] = {
                currentPage: 1,
                pageSize: 10,
            };
        },

        setCurrentPage: (
            state,
            action: PayloadAction<{ module: string; page: number }>
        ) => {
            if (state.selectedPagination[action.payload.module]) {
                state.selectedPagination[action.payload.module].currentPage = action.payload.page;
            }
        },

        setPageSize: (
            state,
            action: PayloadAction<{ module: string; size: number }>
        ) => {
            if (state.selectedPagination[action.payload.module]) {
                state.selectedPagination[action.payload.module].pageSize = action.payload.size;
                state.selectedPagination[action.payload.module].currentPage = 1; // reset al cambiar tamaño
            }
        },
        selectedTypeIndicator: (state, action: PayloadAction<{ typeOfIndicator: { name: string } }>) => {
            if (!action.payload.typeOfIndicator) return;

            state.selected_type_indicator = action.payload.typeOfIndicator;
        },
        clearTypeOfIndicator: (state) => {
            state.selected_type_indicator = null;
        },
    },
    extraReducers: (builder) => {
        builder.addCase(getODS.pending, (state) => {
            state.loadingODS = "pending";
        });
        builder.addCase(getODS.fulfilled, (state, action) => {
            state.loadingODS = "succeeded";
            state.ods = action.payload.ods;
            state.errorsOds = action.payload.error;
        });
        builder.addCase(getODS.rejected, (state) => {
            state.loadingODS = "failed";
            state.errorsOds = true;
        });
        builder.addCase(getPriorityGroups.pending, (state) => {
            state.loadingGroups = "pending";
        });
        builder.addCase(getPriorityGroups.fulfilled, (state, action) => {
            state.loadingGroups = "succeeded";
            state.priorityGroups = action.payload.priority_groups.map(
                (group: OdsPriorityGroupsExtended): OdsPriorityGroupsWithGrouped => {
                    const groupedByTipo: Record<'Estructurales' | 'Proceso' | 'Resultados', any[]> = {
                        Estructurales: [],
                        Proceso: [],
                        Resultados: [],
                    };

                    if (group.indicadores_estructurales) {
                        groupedByTipo.Estructurales = flattenEstructurales(group.indicadores_estructurales);
                    }

                    group.indicadores.forEach((indicator) => {
                        const tipoTitulo = indicator.tipoIndicador?.titulo.toLocaleLowerCase() || "";

                        if (/proceso/i.test(tipoTitulo)) {
                            groupedByTipo.Proceso.push(indicator);
                        } else if (/resultado/i.test(tipoTitulo)) {
                            groupedByTipo.Resultados.push(indicator);
                        }
                    });

                    return {
                        ...group,
                        tipo_indicador: groupedByTipo as Record<'Estructurales' | 'Proceso' | 'Resultados', Indicator[]>,
                        color: "#FF9298",
                    };
                }
            );

            state.errorPriorityGroups = action.payload.error;
        });
        builder.addCase(getPriorityGroups.rejected, (state) => {
            state.loadingGroups = "failed";
            state.errorPriorityGroups = true;
        });
    }
});

export const {
    setSelectedItem,
    setCurrentPage,
    setPageSize,
    selectedTypeIndicator,
    clearTypeOfIndicator
} = OdsPriorityGroupsSlice.actions;

export default OdsPriorityGroupsSlice.reducer;